import { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ExtendedCampaignRead,
  ExtendedLeadsListResponse,
  useDeleteCampaignMutation,
  useGetCampaignDetailsQuery,
  useGetLeadsQuery,
  useUpdateCampaignMutation,
} from '@api/apiCompat';
import { emptyBaseSplitApi } from '@api/baseApi';
import { useGetCampaignLeadsQuery as useGetEmailCampaignLeadsQuery, useGetEmailCampaignQuery } from '@api/emailApi';
import {
  useGetContactFormStatsQuery,
  useGetContactFormStatusQuery,
  useGetEmailPreviewQuery,
  useStartContactFormOutreachMutation,
} from '@api/scraperApi';
import { ROUTING } from '@constants/routing';
import { useCampaignQueueStatus } from '@hooks/api/useCampaignQueueStatus';
import { CampaignDetailsProvider } from '@pages/CampaignDetails/context';
import styled from 'styled-components';

import { EmailCampaignStats, EmailCampaignStatsData } from '@components/EmailCampaignStats';
import EmailReviewPanel from '@components/EmailReviewPanel';
import { LeadDetailsDrawer } from '@components/LeadDetailsDrawer';
import SignalTriggerConfig from '@components/SignalTriggerConfig';
import { Badge } from '@components/theme-v2/components/Badge';
import { Button } from '@components/theme-v2/components/Button';
import { Card, DataTable } from '@components/theme-v2/components/Card';
import { Input } from '@components/theme-v2/components/Input';
// Theme-v2 components
import { PageHeader } from '@components/theme-v2/components/PageHeader';
import { UpgradePromoBanner } from '@components/UpgradePromoBanner';

// Enrichment progress endpoint (uses main API base URL)
const enrichmentApi = emptyBaseSplitApi.injectEndpoints({
  endpoints: build => ({
    getEnrichmentProgress: build.query<
      { total: number; enriched: number; pending: number; percentage: number },
      string
    >({
      query: campaignId => `/api/campaigns/${campaignId}/enrichment-progress/`,
    }),
  }),
  overrideExisting: true,
});
const { useGetEnrichmentProgressQuery } = enrichmentApi;

// Email reveal glow animation
const emailRevealGlow = `
  @keyframes emailReveal {
    0% { background-color: rgba(0, 200, 224, 0.25); box-shadow: 0 0 12px rgba(0, 200, 224, 0.4); }
    50% { background-color: rgba(0, 200, 224, 0.1); box-shadow: 0 0 6px rgba(0, 200, 224, 0.2); }
    100% { background-color: transparent; box-shadow: none; }
  }
`;

const EmailRevealCell = styled.span<{ $animate?: boolean }>`
  ${props => props.$animate && emailRevealGlow}
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--sk-cyan-500);
  ${props =>
    props.$animate &&
    `
    padding: 2px 6px;
    margin: -2px -6px;
    border-radius: 4px;
    animation: emailReveal 2s ease-out forwards;
  `}
`;

// Styled components
const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sk-space-6);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--sk-space-3);
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sk-space-1);
`;

const InfoLabel = styled.span`
  font-size: var(--sk-text-xs);
  color: var(--sk-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const InfoValue = styled.span`
  font-size: var(--sk-text-sm);
  color: var(--sk-text-primary);
`;

// Enrichment Progress

const EnrichmentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

const EnrichmentTitle = styled.span<{ $complete?: boolean }>`
  font-size: var(--sk-text-sm);
  font-weight: 600;
  color: ${props => (props.$complete ? 'var(--sk-green-500)' : 'var(--sk-text-primary)')};
`;

const EnrichmentCount = styled.span<{ $complete?: boolean }>`
  font-family: var(--sk-font-mono);
  font-size: var(--sk-text-base);
  font-weight: 700;
  color: ${props => (props.$complete ? 'var(--sk-green-500)' : 'var(--sk-cyan-500)')};
`;

const EnrichmentBarTrack = styled.div`
  width: 100%;
  height: 22px;
  background: var(--sk-bg-tertiary);
  border-radius: 11px;
  overflow: hidden;
  position: relative;
`;

const EnrichmentBarFill = styled.div<{ $complete?: boolean }>`
  height: 100%;
  border-radius: 11px;
  transition: width 0.8s ease;
  background: ${props =>
    props.$complete ? 'var(--sk-green-500)' : 'linear-gradient(90deg, var(--sk-cyan-500), var(--sk-cyan-400))'};
  position: relative;
  overflow: hidden;
  min-width: 42px;

  ${props =>
    !props.$complete &&
    `
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        90deg,
        transparent 0%,
        rgba(255, 255, 255, 0.15) 50%,
        transparent 100%
      );
      animation: shimmer 1.5s infinite;
    }

    @keyframes shimmer {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
  `}
`;

const EnrichmentBarLabel = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--sk-font-mono);
  font-size: 11px;
  font-weight: 700;
  color: var(--sk-bg-primary);
  z-index: 1;
  pointer-events: none;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
`;

const EnrichmentSubtext = styled.span`
  font-size: var(--sk-text-xs);
  color: var(--sk-text-tertiary);
  text-align: right;
`;

const OutreachRow = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: var(--sk-space-5);
  align-items: stretch;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const OutreachGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: 1fr;
  gap: var(--sk-space-4);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
  }
`;

const OutreachOptionCard = styled.div<{ $variant?: 'cyan' | 'purple' | 'green' | 'default' }>`
  background: ${props =>
    props.$variant === 'cyan'
      ? 'rgba(0, 200, 224, 0.05)'
      : props.$variant === 'purple'
        ? 'rgba(167, 139, 250, 0.05)'
        : props.$variant === 'green'
          ? 'rgba(0, 255, 127, 0.05)'
          : 'var(--sk-surface-default)'};
  border: 1px solid
    ${props =>
      props.$variant === 'cyan'
        ? 'rgba(0, 200, 224, 0.2)'
        : props.$variant === 'purple'
          ? 'rgba(167, 139, 250, 0.2)'
          : props.$variant === 'green'
            ? 'rgba(0, 255, 127, 0.2)'
            : 'var(--sk-surface-border)'};
  border-radius: var(--sk-radius-lg);
  padding: var(--sk-space-4);
  display: flex;
  flex-direction: column;
  gap: var(--sk-space-3);
  overflow: visible;
  position: relative;
`;

const OutreachOptionIcon = styled.div<{ $variant?: 'cyan' | 'purple' | 'green' }>`
  width: 40px;
  height: 40px;
  border-radius: var(--sk-radius-default);
  background: ${props =>
    props.$variant === 'cyan'
      ? 'rgba(0, 200, 224, 0.15)'
      : props.$variant === 'purple'
        ? 'rgba(167, 139, 250, 0.15)'
        : 'rgba(0, 255, 127, 0.15)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props =>
    props.$variant === 'cyan' ? 'var(--sk-cyan-500)' : props.$variant === 'purple' ? '#a78bfa' : 'var(--sk-green-500)'};

  svg {
    width: 20px;
    height: 20px;
  }
`;

const OutreachOptionContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--sk-space-1);
`;

const OutreachOptionTitle = styled.span`
  font-size: var(--sk-text-sm);
  font-weight: 600;
  color: var(--sk-text-primary);
`;

const OutreachOptionCount = styled.span<{ $variant?: 'cyan' | 'purple' | 'green' }>`
  font-family: var(--sk-font-mono);
  font-size: var(--sk-text-xl);
  font-weight: 700;
  color: ${props =>
    props.$variant === 'cyan' ? 'var(--sk-cyan-500)' : props.$variant === 'purple' ? '#a78bfa' : 'var(--sk-green-500)'};
`;

const OutreachOptionDesc = styled.span`
  font-size: var(--sk-text-xs);
  color: var(--sk-text-tertiary);
`;

const OutreachCoverageBar = styled.div`
  width: 100%;
  height: 6px;
  background: var(--sk-bg-tertiary);
  border-radius: 3px;
  overflow: hidden;
`;

const OutreachCoverageFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, var(--sk-cyan-500), var(--sk-green-500));
  border-radius: 3px;
  transition: width 0.5s ease;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: var(--sk-space-2);
  flex-wrap: wrap;
`;

const FiltersRow = styled.div`
  display: flex;
  gap: var(--sk-space-3);
  align-items: flex-end;
  flex-wrap: wrap;
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sk-space-12);
  color: var(--sk-text-secondary);
`;

// Post-Scraping Completion Banner
const CompletionBanner = styled.div`
  background: linear-gradient(135deg, rgba(0, 200, 224, 0.08) 0%, rgba(0, 255, 127, 0.08) 100%);
  border: 1px solid rgba(0, 200, 224, 0.25);
  border-radius: var(--sk-radius-lg);
  padding: var(--sk-space-5) var(--sk-space-6);
  display: flex;
  align-items: center;
  gap: var(--sk-space-5);

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const BannerIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: var(--sk-radius-default);
  background: rgba(0, 200, 224, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: var(--sk-cyan-500);

  svg {
    width: 24px;
    height: 24px;
  }
`;

const BannerContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--sk-space-1);
`;

const BannerTitle = styled.span`
  font-family: var(--sk-font-display);
  font-size: var(--sk-text-base);
  font-weight: 600;
  color: var(--sk-text-primary);
`;

const BannerDescription = styled.span`
  font-size: var(--sk-text-sm);
  color: var(--sk-text-secondary);
`;

const BannerActions = styled.div`
  display: flex;
  gap: var(--sk-space-2);
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const ContactFormStatsRow = styled.div`
  display: flex;
  gap: var(--sk-space-3);
  flex-wrap: wrap;
  margin-top: var(--sk-space-2);
`;

const ContactFormStat = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sk-space-1);
  font-size: var(--sk-text-xs);
  color: var(--sk-text-secondary);
`;

const ContactFormStatValue = styled.span<{ $color?: string }>`
  font-family: var(--sk-font-mono);
  font-weight: 600;
  color: ${props => props.$color || 'var(--sk-text-primary)'};
`;

// Icons
const IconMail = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const IconPlay = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="5,3 19,12 5,21 5,3" />
  </svg>
);

const IconPause = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="6" y="4" width="4" height="16" />
    <rect x="14" y="4" width="4" height="16" />
  </svg>
);

const IconTrash = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3,6 5,6 21,6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const IconDownload = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7,10 12,15 17,10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

// Email metrics interface for leads with email integration
interface EmailMetrics {
  status: 'pending' | 'sent' | 'opened' | 'clicked' | 'replied' | 'bounced' | 'scheduled' | 'sending';
  sent_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  replied_at: string | null;
  current_step: number | null;
  total_steps: number | null;
  sequence_subject: string | null;
}

// Define lead type matching API response
interface Lead {
  id: string | number;
  leads_id?: string;
  business_name?: string;
  name?: string;
  email?: string | string[];
  phone?: string;
  website?: string;
  address?: string;
  city?: string;
  state?: string;
  company?: string;
  rating?: string | number;
  categories?: string | string[];
  source?: string;
  scraper_name?: string;
  source_url?: string;
  created_at: string;
  email_metrics?: EmailMetrics | null;
  additionalData?: {
    additional_emails?: string[];
    additional_phones?: string[];
    additional_contacts_unlocked?: boolean;
    [key: string]: any;
  };
  additional_data?: {
    additional_emails?: string[];
    additional_phones?: string[];
    additional_contacts_unlocked?: boolean;
    [key: string]: any;
  };
  data?: {
    name?: string;
    email?: string | string[];
    phone?: string;
    address?: string;
    city?: string;
    state?: string;
    company?: string;
    rating?: string | number;
    categories?: string | string[];
    website?: string;
    enriched_at?: string;
    additionalData?: {
      additional_emails?: string[];
      additional_phones?: string[];
      additional_contacts_unlocked?: boolean;
    };
  };
}

// Contact Form Start Button with message preview
const ContactFormStartButton: FC<{ campaignId: string; config: any }> = ({ campaignId, config }) => {
  const [startOutreach, { isLoading }] = useStartContactFormOutreachMutation();
  const { data: cfStatus } = useGetContactFormStatusQuery(campaignId, { skip: !campaignId });
  const [showPreview, setShowPreview] = useState(false);
  const [started, setStarted] = useState(false);

  const messageTemplate = config?.message_template || config?.messageTemplate || '';
  const senderName = config?.sender_name || config?.senderName || '';
  const senderEmail = config?.sender_email || config?.senderEmail || '';

  if (cfStatus?.exists && cfStatus.status === 'running') {
    return (
      <Badge variant="green" size="sm">
        Running
      </Badge>
    );
  }
  if (cfStatus?.exists && cfStatus.status === 'completed') {
    return (
      <Badge variant="cyan" size="sm">
        Completed
      </Badge>
    );
  }
  if (started) {
    return (
      <Badge variant="green" size="sm">
        Started
      </Badge>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <Button
        variant="secondary"
        size="sm"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          setShowPreview(!showPreview);
        }}
      >
        {showPreview ? 'Close' : 'Review & Start'}
      </Button>
      {showPreview && (
        <div
          onClick={e => e.stopPropagation()}
          style={{
            position: 'absolute',
            right: 0,
            top: '100%',
            marginTop: 8,
            width: 380,
            background: 'var(--sk-surface-default)',
            border: '1px solid var(--sk-surface-border)',
            borderRadius: 'var(--sk-radius-default)',
            padding: 16,
            zIndex: 100,
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          }}
        >
          <div
            style={{
              fontSize: 'var(--sk-text-xs)',
              fontWeight: 600,
              color: 'var(--sk-text-tertiary)',
              textTransform: 'uppercase',
              marginBottom: 8,
            }}
          >
            Contact Form Message
          </div>
          {messageTemplate ? (
            <div
              style={{
                fontSize: 'var(--sk-text-sm)',
                color: 'var(--sk-text-primary)',
                whiteSpace: 'pre-wrap',
                background: 'var(--sk-bg-secondary)',
                padding: 12,
                borderRadius: 'var(--sk-radius-sm)',
                marginBottom: 12,
                maxHeight: 200,
                overflowY: 'auto',
                lineHeight: 1.6,
              }}
            >
              {messageTemplate}
            </div>
          ) : (
            <div style={{ fontSize: 'var(--sk-text-sm)', color: 'var(--sk-text-tertiary)', marginBottom: 12 }}>
              No message template configured
            </div>
          )}
          {senderName && (
            <div style={{ fontSize: '12px', color: 'var(--sk-text-secondary)', marginBottom: 4 }}>
              From: {senderName} {senderEmail ? `<${senderEmail}>` : ''}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              type="button"
              disabled={isLoading || !messageTemplate}
              onClick={async () => {
                try {
                  await startOutreach(campaignId).unwrap();
                  setStarted(true);
                  setShowPreview(false);
                } catch (err) {
                  console.error('Failed to start contact form outreach:', err);
                }
              }}
              style={{
                padding: '6px 16px',
                borderRadius: 'var(--sk-radius-sm)',
                border: 'none',
                background: messageTemplate ? '#7c3aed' : 'var(--sk-bg-tertiary)',
                color: messageTemplate ? '#fff' : 'var(--sk-text-tertiary)',
                fontSize: 'var(--sk-text-xs)',
                fontWeight: 600,
                cursor: messageTemplate ? 'pointer' : 'not-allowed',
              }}
            >
              {isLoading ? 'Starting...' : 'Start Submissions'}
            </button>
            <button
              type="button"
              onClick={() => setShowPreview(false)}
              style={{
                padding: '6px 16px',
                borderRadius: 'var(--sk-radius-sm)',
                border: '1px solid var(--sk-surface-border)',
                background: 'var(--sk-bg-secondary)',
                color: 'var(--sk-text-secondary)',
                fontSize: 'var(--sk-text-xs)',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const CampaignDetails: FC = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [revealedEmails, setRevealedEmails] = useState<Set<string | number>>(new Set());
  const prevLeadEmailsRef = useRef<Map<string | number, string>>(new Map());

  // Fetch campaign details from Django (scraper campaigns)
  const {
    data: rawCampaign,
    isLoading: djangoLoading,
    error: djangoError,
  } = useGetCampaignDetailsQuery(campaignId ? { id: campaignId } : ({} as any), {
    skip: !campaignId,
    pollingInterval: 30000,
  });

  // If Django fails (e.g. email campaign UUID not found), try the email service
  const djangoFailed = !!djangoError;
  const {
    data: emailCampaignData,
    isLoading: emailLoading,
    error: emailError,
  } = useGetEmailCampaignQuery(campaignId || '', { skip: !campaignId || !djangoFailed });

  // Use whichever source returned data; adapt email campaign to the expected shape
  const campaignLoading = djangoLoading || (djangoFailed && emailLoading);
  const campaignError = djangoFailed ? emailError : undefined;
  const isEmailCampaign = djangoFailed && !!emailCampaignData;

  const campaign = isEmailCampaign
    ? ({
        id: emailCampaignData.id,
        name: emailCampaignData.name,
        description: '',
        status: emailCampaignData.status?.toLowerCase() || 'draft',
        total_leads: emailCampaignData.leadsCount || emailCampaignData.leads?.length || 0,
        leads_count: emailCampaignData.leadsCount || emailCampaignData.leads?.length || 0,
        created_at: emailCampaignData.createdAt,
        updated_at: emailCampaignData.updatedAt || emailCampaignData.createdAt,
        config: { mode: 'email-only' },
        campaign_type: { type_name: 'Email Campaign' },
      } as unknown as ExtendedCampaignRead)
    : (rawCampaign as ExtendedCampaignRead | undefined);

  // Fetch leads for this campaign — use Django leads API for scraper campaigns,
  // email service leads API for email campaigns
  const { data: rawLeadsResponse, isLoading: djangoLeadsLoading } = useGetLeadsQuery(
    {
      campaign_id: campaignId,
      page,
      page_size: 25,
    } as any,
    { skip: !campaignId || isEmailCampaign, pollingInterval: 30000 },
  );

  // For email campaigns, fetch leads from the email campaigns leads endpoint (paginated)
  const { data: emailLeadsResponse, isLoading: emailLeadsLoading } = useGetEmailCampaignLeadsQuery(
    { campaignId: campaignId || '', page, page_size: 25 },
    { skip: !campaignId || !isEmailCampaign, pollingInterval: 30000 },
  );

  // Cast to extended type with pagination properties
  const leadsResponse = rawLeadsResponse as (ExtendedLeadsListResponse | Lead[]) | undefined;
  const leadsLoading = isEmailCampaign ? emailLeadsLoading : djangoLeadsLoading;

  const [updateCampaign] = useUpdateCampaignMutation();
  const [deleteCampaign] = useDeleteCampaignMutation();

  // Fetch contact form stats for this campaign
  const { data: contactFormStats } = useGetContactFormStatsQuery(campaignId || '', {
    skip: !campaignId,
  });

  // Enrichment progress — poll every 20s while leads are still pending
  const { data: enrichmentProgress } = useGetEnrichmentProgressQuery(campaignId || '', {
    skip: !campaignId || isEmailCampaign,
    pollingInterval: 20000,
  });

  // Queue position + ETA + tier-aware upgrade CTA data (Phase 1).
  // Only relevant for scraping campaigns; email campaigns aren't queued
  // through the scraper dispatcher.
  const { data: queueStatus } = useCampaignQueueStatus(campaignId, {
    enabled: Boolean(campaignId) && !isEmailCampaign,
  });

  // Check if email sequences exist (for the review panel)
  const { data: emailPreviewData } = useGetEmailPreviewQuery(
    { campaignId: campaignId || '', page: 1, pageSize: 1 },
    { skip: !campaignId },
  );
  const hasEmailSequences = (emailPreviewData?.sequences?.length ?? 0) > 0;

  // Handle leads from either Django or email service
  // Backend returns { count, next, previous, results: [...] } for paginated response
  // or a flat array if pagination isn't enabled
  const emailLeadsRaw = emailLeadsResponse as any;
  const emailLeadsList = emailLeadsRaw?.results || (Array.isArray(emailLeadsRaw) ? emailLeadsRaw : []);
  const leads: Lead[] = isEmailCampaign
    ? emailLeadsList.map((l: any) => ({
        id: l.id,
        business_name: l.company || '',
        name: [l.first_name || l.firstName, l.last_name || l.lastName].filter(Boolean).join(' ') || '',
        email: l.email || '',
        phone: l.phone || '',
        city: l.custom_fields?.city || '',
        state: l.custom_fields?.state || '',
        created_at: l.created_at,
        data: {
          ...l,
          email: l.email,
          name: [l.first_name || l.firstName, l.last_name || l.lastName].filter(Boolean).join(' '),
          company: l.company,
          phone: l.phone,
          email_status: l.status || 'pending',
          current_step: l.current_sequence_index || 0,
          sent_at: l.sent_at,
          opened_at: l.opened_at,
          clicked_at: l.clicked_at,
          replied_at: l.replied_at,
          created_at: l.created_at,
        },
      }))
    : Array.isArray(leadsResponse)
      ? leadsResponse
      : (leadsResponse as ExtendedLeadsListResponse)?.results ||
        (leadsResponse as ExtendedLeadsListResponse)?.data ||
        [];
  const totalLeads = isEmailCampaign
    ? emailLeadsRaw?.count || emailCampaignData?.leadsCount || emailLeadsList.length || 0
    : (leadsResponse as ExtendedLeadsListResponse)?.total ||
      (leadsResponse as ExtendedLeadsListResponse)?.count ||
      leads.length;

  // Track email reveals — detect leads that gained an email between polls
  useEffect(() => {
    if (!leads.length) return;
    const newReveals = new Set<string | number>();
    const currentMap = new Map<string | number, string>();

    leads.forEach(lead => {
      const email = Array.isArray(lead.email) ? lead.email[0] : lead.email || lead.data?.email || '';
      const emailStr = Array.isArray(email) ? email[0] : email || '';
      currentMap.set(lead.id, emailStr);

      const prevEmail = prevLeadEmailsRef.current.get(lead.id);
      // Lead existed before with no email, now has one
      if (prevEmail !== undefined && !prevEmail && emailStr) {
        newReveals.add(lead.id);
      }
    });

    if (newReveals.size > 0) {
      setRevealedEmails(newReveals);
      // Clear glow after animation completes
      const timer = setTimeout(() => setRevealedEmails(new Set()), 2500);
      return () => clearTimeout(timer);
    }

    prevLeadEmailsRef.current = currentMap;
  }, [leads]);

  // Check if campaign has email integration
  const campaignConfig = campaign?.config as any;
  const mode = campaignConfig?.mode || '';
  const hasEmailIntegration = !!(
    isEmailCampaign ||
    mode === 'email-only' ||
    mode === 'scrape-and-email' ||
    mode === 'scrape-email-and-forms' ||
    campaignConfig?.email_integration?.email_campaign_id
  );
  const emailCampaignId = campaignConfig?.email_integration?.email_campaign_id || campaignConfig?.email_campaign_id;

  // Extract email campaign stats from API response (if available)
  const emailCampaignStats: EmailCampaignStatsData | null = (() => {
    const emailCampaign = (leadsResponse as any)?.email_campaign;
    if (emailCampaign?.stats) {
      return emailCampaign.stats;
    }
    // Compute stats from leads if API doesn't provide aggregate
    if (hasEmailIntegration && leads.length > 0) {
      const leadsWithMetrics = leads.filter(l => l.email_metrics);
      const sent = leadsWithMetrics.filter(l => l.email_metrics?.sent_at).length;
      const opened = leadsWithMetrics.filter(l => l.email_metrics?.opened_at).length;
      const clicked = leadsWithMetrics.filter(l => l.email_metrics?.clicked_at).length;
      const replied = leadsWithMetrics.filter(l => l.email_metrics?.replied_at).length;
      const bounced = leadsWithMetrics.filter(l => l.email_metrics?.status === 'bounced').length;
      return {
        total: totalLeads,
        sent,
        opened,
        clicked,
        replied,
        bounced,
        open_rate: sent > 0 ? Math.round((opened / sent) * 100) : 0,
        click_rate: sent > 0 ? Math.round((clicked / sent) * 100) : 0,
        reply_rate: sent > 0 ? Math.round((replied / sent) * 100) : 0,
      };
    }
    return null;
  })();

  // Normalize status
  const getStatusText = (status: string | number): string => {
    if (typeof status === 'number') {
      switch (status) {
        case 0:
          return 'Draft';
        case 1:
          return 'Active';
        case 2:
          return 'Completed';
        case 3:
          return 'Failed';
        case 4:
          return 'Paused';
        default:
          return 'Unknown';
      }
    }
    return String(status).charAt(0).toUpperCase() + String(status).slice(1);
  };

  // Map status to PageHeader status type (active | warning | error | idle)
  const getHeaderStatus = (status: string | number): 'active' | 'warning' | 'error' | 'idle' => {
    if (typeof status === 'number') {
      switch (status) {
        case 1:
          return 'active';
        case 2:
          return 'idle'; // completed
        case 3:
          return 'error'; // failed
        case 4:
          return 'warning'; // paused
        default:
          return 'idle';
      }
    }
    const s = String(status).toLowerCase();
    if (s === 'active') return 'active';
    if (s === 'completed') return 'idle';
    if (s === 'failed') return 'error';
    if (s === 'paused') return 'warning';
    return 'idle';
  };

  const campaignStatus = campaign?.status ?? 'draft';
  const isActive = String(campaignStatus) === '1' || campaignStatus === 'active';
  const isCompleted = String(campaignStatus) === '2' || campaignStatus === 'completed';

  // Helper to get lead field value (handles both flat and nested data)
  const getLeadValue = useCallback((lead: Lead, field: string): string => {
    // Try flat field first, then nested data field
    const value = (lead as any)[field] || lead.data?.[field as keyof typeof lead.data] || '';
    // Handle array values (like email)
    if (Array.isArray(value)) {
      return value[0] || '';
    }
    return String(value);
  }, []);

  // Build full address from lead data
  const getFullAddress = useCallback(
    (lead: Lead): string => {
      const address = getLeadValue(lead, 'address');
      const city = getLeadValue(lead, 'city');
      const state = getLeadValue(lead, 'state');

      // Combine address components
      const parts = [address, city, state].filter(Boolean);
      return parts.join(', ') || '';
    },
    [getLeadValue],
  );

  // Check if lead has additional contacts
  const getAdditionalContactsCount = useCallback((lead: Lead): number => {
    const additionalData = lead.additionalData || lead.additional_data || lead.data?.additionalData || {};
    const additionalEmails = additionalData.additional_emails || [];
    const additionalPhones = additionalData.additional_phones || [];
    return additionalEmails.length + additionalPhones.length;
  }, []);

  // Handle row click to open drawer
  const handleRowClick = useCallback((lead: Lead) => {
    setSelectedLead(lead);
    setDrawerOpen(true);
  }, []);

  // Handle drawer close
  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const handleToggleStatus = async () => {
    if (campaign && campaignId) {
      await updateCampaign({
        id: campaignId,
        patchedCampaignRequest: {
          status: isActive ? 4 : 1, // Toggle between active and paused
        },
      } as any);
    }
  };

  const handleDelete = async () => {
    if (campaignId && window.confirm('Are you sure you want to delete this campaign?')) {
      await deleteCampaign({ id: campaignId });
      navigate(`/${ROUTING.ALL_CAMPAIGNS}`);
    }
  };

  // Filter leads by search term
  const filteredLeads = leads.filter(lead => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const emailStr = Array.isArray(lead.email) ? lead.email[0] : lead.email;
    return (
      lead.business_name?.toLowerCase().includes(term) ||
      lead.name?.toLowerCase().includes(term) ||
      emailStr?.toLowerCase().includes(term) ||
      lead.phone?.includes(term)
    );
  });

  if (campaignLoading) {
    return <LoadingWrapper>Loading campaign...</LoadingWrapper>;
  }

  if (campaignError) {
    const status = (campaignError as any)?.status;
    if (status === 403) {
      return (
        <LoadingWrapper>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 'var(--sk-text-lg)', marginBottom: 'var(--sk-space-2)' }}>Access Denied</div>
            <div style={{ color: 'var(--sk-text-tertiary)' }}>You do not have permission to view this campaign.</div>
          </div>
        </LoadingWrapper>
      );
    }
    if (status === 401) {
      return (
        <LoadingWrapper>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 'var(--sk-text-lg)', marginBottom: 'var(--sk-space-2)' }}>
              Authentication Required
            </div>
            <div style={{ color: 'var(--sk-text-tertiary)' }}>Please log in to view this campaign.</div>
          </div>
        </LoadingWrapper>
      );
    }
    return (
      <LoadingWrapper>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 'var(--sk-text-lg)', marginBottom: 'var(--sk-space-2)' }}>Error Loading Campaign</div>
          <div style={{ color: 'var(--sk-text-tertiary)' }}>Something went wrong. Please try again.</div>
        </div>
      </LoadingWrapper>
    );
  }

  if (!campaign) {
    return <LoadingWrapper>Campaign not found</LoadingWrapper>;
  }

  return (
    <>
      <Helmet>
        <title>{campaign?.name || 'Campaign Details'} - ScrapeKing</title>
      </Helmet>
      <CampaignDetailsProvider>
        <PageWrapper>
          <PageHeader
            title={campaign?.name || 'Campaign'}
            subtitle={campaign?.description || 'View campaign details and leads'}
            breadcrumbs={[
              { label: 'Home', href: '/' },
              { label: 'All Campaigns', href: `/${ROUTING.ALL_CAMPAIGNS}` },
              { label: campaign?.name || 'Campaign' },
            ]}
            status={getHeaderStatus(campaignStatus)}
            statusText={getStatusText(campaignStatus)}
            actions={
              <ActionButtons>
                <Button
                  variant={isActive ? 'danger' : 'primary'}
                  icon={isActive ? <IconPause /> : <IconPlay />}
                  onClick={handleToggleStatus}
                >
                  {isActive ? 'Pause' : 'Start'}
                </Button>
                <Button variant="secondary" icon={<IconDownload />}>
                  Export
                </Button>
                <Button variant="ghost" icon={<IconTrash />} onClick={handleDelete}>
                  Delete
                </Button>
              </ActionButtons>
            }
          />

          {/* Queue-position upgrade CTA (Phase 1) — shown when campaign is
              still pending in the dispatcher and the org isn't on a VIP
              tier yet. Banner is dismissible (24h TTL, localStorage). */}
          {queueStatus && queueStatus.position > 0 && (
            <UpgradePromoBanner
              currentTier={queueStatus.tier}
              unlockedAtTier={queueStatus.priority_unlocked_at_tier}
              orgId={(campaign as any)?.organization_id ?? (campaign as any)?.organization}
              position={queueStatus.position}
              etaSeconds={queueStatus.eta_seconds}
            />
          )}

          {/* Email Review Panel - shown when campaign has email sequences configured.
              Continuous (perpetual) campaigns auto-send each wave's leads via NestJS,
              so the per-batch authorize step doesn't apply and would be misleading. */}
          {totalLeads > 0 && hasEmailSequences && campaignId && !(campaign as any)?.config?.perpetual?.enabled && (
            <div id="email-review-panel">
              <EmailReviewPanel campaignId={campaignId} />
            </div>
          )}

          {/* Post-Scraping Completion Banner - shown when completed, no sequences, no email integration */}
          {isCompleted && totalLeads > 0 && !hasEmailIntegration && !hasEmailSequences && (
            <CompletionBanner>
              <BannerIcon>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </BannerIcon>
              <BannerContent>
                <BannerTitle>Scraping Complete — {totalLeads.toLocaleString()} leads collected</BannerTitle>
                <BannerDescription>
                  {((campaign as any)?.emails_found ?? 0) > 0
                    ? `${((campaign as any)?.emails_found ?? 0).toLocaleString()} leads have email addresses ready for outreach. Set up an email campaign to start reaching them.`
                    : 'Your leads are ready. Set up an email campaign or contact form outreach to start reaching them.'}
                </BannerDescription>
              </BannerContent>
              <BannerActions>
                <Button variant="primary" icon={<IconMail />} onClick={() => navigate(`/${ROUTING.EMAIL_CAMPAIGNS}`)}>
                  Start Email Campaign
                </Button>
              </BannerActions>
            </CompletionBanner>
          )}

          {/* Post-Scraping Banner with active email - guide to mailbox */}
          {isCompleted && totalLeads > 0 && hasEmailIntegration && (
            <CompletionBanner>
              <BannerIcon>
                <IconMail />
              </BannerIcon>
              <BannerContent>
                <BannerTitle>Email Campaign Active</BannerTitle>
                <BannerDescription>
                  Emails are being sent to your leads. Check your unified inbox for replies and manage conversations.
                </BannerDescription>
              </BannerContent>
              <BannerActions>
                <Button variant="primary" icon={<IconMail />} onClick={() => navigate(`/${ROUTING.MAILBOX}`)}>
                  Open Inbox
                </Button>
                <Button variant="secondary" onClick={() => navigate(`/${ROUTING.EMAIL_ANALYTICS}`)}>
                  View Analytics
                </Button>
              </BannerActions>
            </CompletionBanner>
          )}

          {/* Email Campaign Stats - shown when email integration is enabled */}
          {hasEmailIntegration && (
            <EmailCampaignStats stats={emailCampaignStats} loading={leadsLoading} emailCampaignId={emailCampaignId} />
          )}

          {/* Signal Triggers - configurable per campaign */}
          {hasEmailIntegration && emailCampaignId && <SignalTriggerConfig campaignId={emailCampaignId} />}

          {/* Outreach Options Panel with Campaign Info card */}
          {totalLeads > 0 &&
            (() => {
              const leadsWithEmail = (campaign as any)?.emails_found ?? 0;
              const leadsWithWebsiteNoEmail = (campaign as any)?.website_leads ?? 0;
              const hasContactFormConfig = !!(
                campaignConfig?.contact_form_config?.enabled ||
                campaignConfig?.contact_form?.enabled ||
                mode === 'scrape-email-and-forms'
              );

              return (
                <OutreachRow>
                  {/* Campaign Summary Card */}
                  <Card>
                    <Card.Body>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sk-space-3)' }}>
                        {/* Stats in 2-col grid */}
                        <InfoGrid>
                          <InfoItem>
                            <InfoLabel>Total Leads</InfoLabel>
                            <div
                              style={{
                                fontFamily: 'var(--sk-font-mono)',
                                fontSize: '22px',
                                fontWeight: 700,
                                color: 'var(--sk-cyan-500)',
                                lineHeight: 1.1,
                              }}
                            >
                              {totalLeads.toLocaleString()}
                            </div>
                          </InfoItem>
                          <InfoItem>
                            <InfoLabel>Tokens Used</InfoLabel>
                            <div
                              style={{
                                fontFamily: 'var(--sk-font-mono)',
                                fontSize: '22px',
                                fontWeight: 700,
                                color: 'var(--sk-text-tertiary)',
                                lineHeight: 1.1,
                              }}
                            >
                              {(campaign?.used_tokens || 0).toLocaleString()}
                            </div>
                          </InfoItem>
                          <InfoItem>
                            <InfoLabel>Campaign ID</InfoLabel>
                            <InfoValue
                              style={{
                                fontFamily: 'var(--sk-font-mono)',
                                fontSize: 'var(--sk-text-xs)',
                                cursor: 'pointer',
                              }}
                              title={`Click to copy: ${campaignId}`}
                              onClick={() => navigator.clipboard.writeText(campaignId || '')}
                            >
                              {campaignId?.slice(-6)}
                            </InfoValue>
                          </InfoItem>
                          <InfoItem>
                            <InfoLabel>Created</InfoLabel>
                            <InfoValue>
                              {campaign?.created_at ? new Date(campaign.created_at).toLocaleDateString() : 'N/A'}
                            </InfoValue>
                          </InfoItem>
                          {queueStatus && queueStatus.position > 0 && (
                            <InfoItem>
                              <InfoLabel>Queue Position</InfoLabel>
                              <InfoValue>
                                #{queueStatus.position}{' '}
                                <span style={{ color: 'var(--sk-text-tertiary)', fontSize: '0.85em' }}>
                                  of {queueStatus.total_pending}
                                  {queueStatus.eta_seconds > 0 && (
                                    <>
                                      {' '}
                                      · ~
                                      {queueStatus.eta_seconds < 60
                                        ? `${queueStatus.eta_seconds}s`
                                        : queueStatus.eta_seconds < 3600
                                          ? `${Math.round(queueStatus.eta_seconds / 60)}min`
                                          : `${Math.round(queueStatus.eta_seconds / 3600)}h`}
                                    </>
                                  )}
                                </span>
                              </InfoValue>
                            </InfoItem>
                          )}
                        </InfoGrid>

                        {/* Enrichment Progress */}
                        {enrichmentProgress && enrichmentProgress.total > 0 && (
                          <div>
                            <EnrichmentHeader>
                              <EnrichmentTitle $complete={enrichmentProgress.pending === 0}>
                                {enrichmentProgress.pending === 0 ? 'All Leads Enriched' : 'Processing'}
                              </EnrichmentTitle>
                              <EnrichmentCount $complete={enrichmentProgress.pending === 0}>
                                {enrichmentProgress.enriched.toLocaleString()} /{' '}
                                {enrichmentProgress.total.toLocaleString()}
                              </EnrichmentCount>
                            </EnrichmentHeader>
                            <EnrichmentBarTrack style={{ marginTop: 'var(--sk-space-2)' }}>
                              <EnrichmentBarFill
                                $complete={enrichmentProgress.pending === 0}
                                style={{ width: `${enrichmentProgress.percentage}%` }}
                              >
                                <EnrichmentBarLabel>{enrichmentProgress.percentage}%</EnrichmentBarLabel>
                              </EnrichmentBarFill>
                            </EnrichmentBarTrack>
                            <EnrichmentSubtext style={{ marginTop: 'var(--sk-space-1)' }}>
                              {enrichmentProgress.pending === 0
                                ? 'enrichment complete'
                                : `${enrichmentProgress.pending.toLocaleString()} remaining`}
                            </EnrichmentSubtext>
                          </div>
                        )}
                      </div>
                    </Card.Body>
                  </Card>

                  {/* Outreach cards — no header, just the 3 cards */}
                  <OutreachGrid>
                    {/* Email Option */}
                    <OutreachOptionCard
                      $variant="cyan"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/${ROUTING.EMAIL_CAMPAIGNS}`)}
                    >
                      <OutreachOptionIcon $variant="cyan">
                        <IconMail />
                      </OutreachOptionIcon>
                      <OutreachOptionContent>
                        <OutreachOptionTitle>Email Campaign</OutreachOptionTitle>
                        <OutreachOptionCount $variant="cyan">{leadsWithEmail} leads</OutreachOptionCount>
                        <OutreachOptionDesc>Leads with verified email addresses</OutreachOptionDesc>
                      </OutreachOptionContent>
                      {hasEmailIntegration ? (
                        <Badge variant="green" size="sm">
                          Active
                        </Badge>
                      ) : hasEmailSequences ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            document.getElementById('email-review-panel')?.scrollIntoView({ behavior: 'smooth' });
                          }}
                        >
                          Review & Send
                        </Button>
                      ) : (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            navigate(`/${ROUTING.EMAIL_CAMPAIGNS}`);
                          }}
                        >
                          Configure
                        </Button>
                      )}
                    </OutreachOptionCard>

                    {/* Contact Forms Option */}
                    <OutreachOptionCard $variant="purple">
                      <OutreachOptionIcon $variant="purple">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          width="20"
                          height="20"
                        >
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                        </svg>
                      </OutreachOptionIcon>
                      <OutreachOptionContent>
                        <OutreachOptionTitle>Contact Form Outreach</OutreachOptionTitle>
                        <OutreachOptionCount $variant="purple">
                          {contactFormStats?.exists
                            ? `${contactFormStats.submissions_successful}/${contactFormStats.submissions_attempted}`
                            : `${leadsWithWebsiteNoEmail} leads`}
                        </OutreachOptionCount>
                        <OutreachOptionDesc>
                          {contactFormStats?.exists
                            ? 'Submissions successful / attempted'
                            : 'Leads with websites but no email'}
                        </OutreachOptionDesc>
                        {contactFormStats?.exists && (
                          <ContactFormStatsRow>
                            <ContactFormStat>
                              <ContactFormStatValue $color="var(--sk-green-500)">
                                {contactFormStats.submissions_successful}
                              </ContactFormStatValue>
                              sent
                            </ContactFormStat>
                            <ContactFormStat>
                              <ContactFormStatValue $color="#f44336">
                                {contactFormStats.submissions_failed}
                              </ContactFormStatValue>
                              failed
                            </ContactFormStat>
                            <ContactFormStat>
                              <ContactFormStatValue $color="#ff9800">
                                {contactFormStats.submissions_blocked}
                              </ContactFormStatValue>
                              blocked
                            </ContactFormStat>
                          </ContactFormStatsRow>
                        )}
                      </OutreachOptionContent>
                      {contactFormStats?.exists ? (
                        <Badge
                          variant={
                            contactFormStats.status === 'running'
                              ? 'green'
                              : contactFormStats.status === 'completed'
                                ? 'cyan'
                                : 'amber'
                          }
                          size="sm"
                        >
                          {contactFormStats.status.charAt(0).toUpperCase() + contactFormStats.status.slice(1)}
                        </Badge>
                      ) : hasContactFormConfig ? (
                        <ContactFormStartButton
                          campaignId={campaignId || ''}
                          config={campaignConfig?.contact_form_config || campaignConfig?.contact_form || {}}
                        />
                      ) : (
                        <Badge variant="amber" size="sm">
                          Not Configured
                        </Badge>
                      )}
                    </OutreachOptionCard>

                    {/* Combined Stats */}
                    <OutreachOptionCard $variant="green">
                      <OutreachOptionIcon $variant="green">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          width="20"
                          height="20"
                        >
                          <polyline points="22,12 18,12 15,21 9,3 6,12 2,12" />
                        </svg>
                      </OutreachOptionIcon>
                      <OutreachOptionContent>
                        <OutreachOptionTitle>Total Reachable</OutreachOptionTitle>
                        <OutreachOptionCount $variant="green">
                          {leadsWithEmail + leadsWithWebsiteNoEmail} leads
                        </OutreachOptionCount>
                        <OutreachOptionDesc>
                          {Math.round(((leadsWithEmail + leadsWithWebsiteNoEmail) / Math.max(totalLeads, 1)) * 100)}%
                          coverage
                        </OutreachOptionDesc>
                      </OutreachOptionContent>
                      <OutreachCoverageBar>
                        <OutreachCoverageFill
                          style={{
                            width: `${Math.round(((leadsWithEmail + leadsWithWebsiteNoEmail) / Math.max(totalLeads, 1)) * 100)}%`,
                          }}
                        />
                      </OutreachCoverageBar>
                    </OutreachOptionCard>
                  </OutreachGrid>
                </OutreachRow>
              );
            })()}

          {/* Full-Width Leads Table */}
          <DataTable.Card>
            <Card.Header>
              <Card.Title>Leads ({totalLeads})</Card.Title>
            </Card.Header>

            {/* Search & Filters */}
            <div style={{ padding: 'var(--sk-space-4)', borderBottom: '1px solid var(--sk-surface-border)' }}>
              <FiltersRow>
                <Input
                  placeholder="Search leads..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  style={{ maxWidth: '300px' }}
                />
              </FiltersRow>
            </div>

            <DataTable.Wrapper>
              <DataTable.Table>
                <DataTable.Head>
                  <tr>
                    <DataTable.HeadCell>Business</DataTable.HeadCell>
                    <DataTable.HeadCell>Location</DataTable.HeadCell>
                    <DataTable.HeadCell>Email</DataTable.HeadCell>
                    {hasEmailIntegration && (
                      <>
                        <DataTable.HeadCell>Email Status</DataTable.HeadCell>
                        <DataTable.HeadCell>Step</DataTable.HeadCell>
                        <DataTable.HeadCell>Sent</DataTable.HeadCell>
                        <DataTable.HeadCell>Opened</DataTable.HeadCell>
                        <DataTable.HeadCell>Replied</DataTable.HeadCell>
                      </>
                    )}
                    <DataTable.HeadCell>Phone</DataTable.HeadCell>
                    <DataTable.HeadCell>Added</DataTable.HeadCell>
                  </tr>
                </DataTable.Head>
                <DataTable.Body>
                  {leadsLoading ? (
                    <DataTable.Row>
                      <DataTable.Cell colSpan={hasEmailIntegration ? 10 : 5} style={{ textAlign: 'center' }}>
                        Loading leads...
                      </DataTable.Cell>
                    </DataTable.Row>
                  ) : filteredLeads.length === 0 ? (
                    <DataTable.Row>
                      <DataTable.Cell
                        colSpan={hasEmailIntegration ? 10 : 5}
                        style={{ textAlign: 'center', color: 'var(--sk-text-tertiary)' }}
                      >
                        No leads found for this campaign.
                      </DataTable.Cell>
                    </DataTable.Row>
                  ) : (
                    filteredLeads.map(lead => {
                      const businessName =
                        getLeadValue(lead, 'name') || getLeadValue(lead, 'business_name') || 'Unknown';
                      const email = getLeadValue(lead, 'email');
                      const phone = getLeadValue(lead, 'phone');
                      const fullAddress = getFullAddress(lead);

                      return (
                        <DataTable.Row key={lead.id} onClick={() => handleRowClick(lead)} style={{ cursor: 'pointer' }}>
                          <DataTable.Cell>
                            <strong style={{ color: 'var(--sk-text-primary)' }}>{businessName}</strong>
                          </DataTable.Cell>
                          <DataTable.Cell>
                            {fullAddress ? (
                              <span style={{ color: 'var(--sk-text-secondary)' }}>{fullAddress}</span>
                            ) : (
                              <span style={{ color: 'var(--sk-text-tertiary)' }}>—</span>
                            )}
                          </DataTable.Cell>
                          <DataTable.Cell>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              {email ? (
                                <EmailRevealCell $animate={revealedEmails.has(lead.id)}>{email}</EmailRevealCell>
                              ) : (
                                (() => {
                                  const enrichedAt = lead.data?.enriched_at;
                                  const hasWebsite = getLeadValue(lead, 'website');
                                  const hasName = getLeadValue(lead, 'name') || getLeadValue(lead, 'business_name');
                                  const isEnriching = !enrichedAt && (hasWebsite || hasName);
                                  return isEnriching ? (
                                    <span
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        color: 'var(--sk-text-tertiary)',
                                      }}
                                    >
                                      <span
                                        style={{
                                          width: '12px',
                                          height: '12px',
                                          border: '2px solid var(--sk-cyan-500)',
                                          borderTopColor: 'transparent',
                                          borderRadius: '50%',
                                          animation: 'sk-spin 1s linear infinite',
                                          display: 'inline-block',
                                          flexShrink: 0,
                                        }}
                                      />
                                      <span style={{ fontStyle: 'italic', fontSize: '12px' }}>Finding email...</span>
                                    </span>
                                  ) : (
                                    <span style={{ color: 'var(--sk-text-tertiary)' }}>—</span>
                                  );
                                })()
                              )}
                              {getAdditionalContactsCount(lead) > 0 && (
                                <span
                                  style={{
                                    fontSize: '10px',
                                    fontWeight: 600,
                                    padding: '2px 6px',
                                    borderRadius: '10px',
                                    backgroundColor: 'rgba(255, 183, 77, 0.2)',
                                    color: '#ffb74d',
                                    whiteSpace: 'nowrap',
                                  }}
                                  title="Click row to view additional contacts"
                                >
                                  +{getAdditionalContactsCount(lead)} more
                                </span>
                              )}
                            </div>
                          </DataTable.Cell>
                          {hasEmailIntegration && (
                            <>
                              {/* Email Status */}
                              <DataTable.Cell>
                                {lead.email_metrics ? (
                                  <span
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      padding: '2px 8px',
                                      borderRadius: '12px',
                                      fontSize: '11px',
                                      fontWeight: 500,
                                      backgroundColor:
                                        lead.email_metrics.status === 'replied'
                                          ? 'rgba(0, 188, 212, 0.15)'
                                          : lead.email_metrics.status === 'opened'
                                            ? 'rgba(76, 175, 80, 0.15)'
                                            : lead.email_metrics.status === 'clicked'
                                              ? 'rgba(156, 39, 176, 0.15)'
                                              : lead.email_metrics.status === 'sent'
                                                ? 'rgba(33, 150, 243, 0.15)'
                                                : lead.email_metrics.status === 'bounced'
                                                  ? 'rgba(244, 67, 54, 0.15)'
                                                  : 'rgba(158, 158, 158, 0.15)',
                                      color:
                                        lead.email_metrics.status === 'replied'
                                          ? '#00bcd4'
                                          : lead.email_metrics.status === 'opened'
                                            ? '#4caf50'
                                            : lead.email_metrics.status === 'clicked'
                                              ? '#9c27b0'
                                              : lead.email_metrics.status === 'sent'
                                                ? '#2196f3'
                                                : lead.email_metrics.status === 'bounced'
                                                  ? '#f44336'
                                                  : '#9e9e9e',
                                    }}
                                  >
                                    {lead.email_metrics.status.charAt(0).toUpperCase() +
                                      lead.email_metrics.status.slice(1)}
                                  </span>
                                ) : (
                                  <span style={{ color: 'var(--sk-text-tertiary)' }}>—</span>
                                )}
                              </DataTable.Cell>
                              {/* Sequence Step */}
                              <DataTable.Cell>
                                {lead.email_metrics?.current_step ? (
                                  <span
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      padding: '2px 8px',
                                      borderRadius: '12px',
                                      fontSize: '11px',
                                      fontWeight: 500,
                                      backgroundColor: 'rgba(255, 152, 0, 0.15)',
                                      color: '#ff9800',
                                    }}
                                    title={lead.email_metrics.sequence_subject || ''}
                                  >
                                    {lead.email_metrics.current_step}/{lead.email_metrics.total_steps || '?'}
                                  </span>
                                ) : (
                                  <span style={{ color: 'var(--sk-text-tertiary)' }}>—</span>
                                )}
                              </DataTable.Cell>
                              {/* Sent Date */}
                              <DataTable.Cell>
                                {lead.email_metrics?.sent_at ? (
                                  <span style={{ color: '#2196f3', fontSize: 'var(--sk-text-xs)' }}>
                                    {new Date(lead.email_metrics.sent_at).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                    })}
                                  </span>
                                ) : (
                                  <span style={{ color: 'var(--sk-text-tertiary)' }}>—</span>
                                )}
                              </DataTable.Cell>
                              {/* Opened Date */}
                              <DataTable.Cell>
                                {lead.email_metrics?.opened_at ? (
                                  <span style={{ color: '#4caf50', fontSize: 'var(--sk-text-xs)' }}>
                                    {new Date(lead.email_metrics.opened_at).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                    })}
                                  </span>
                                ) : (
                                  <span style={{ color: 'var(--sk-text-tertiary)' }}>—</span>
                                )}
                              </DataTable.Cell>
                              {/* Replied Date */}
                              <DataTable.Cell>
                                {lead.email_metrics?.replied_at ? (
                                  <span
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      padding: '2px 8px',
                                      borderRadius: '12px',
                                      fontSize: '11px',
                                      fontWeight: 600,
                                      backgroundColor: 'rgba(0, 188, 212, 0.15)',
                                      color: '#00bcd4',
                                    }}
                                  >
                                    {new Date(lead.email_metrics.replied_at).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                    })}
                                  </span>
                                ) : (
                                  <span style={{ color: 'var(--sk-text-tertiary)' }}>—</span>
                                )}
                              </DataTable.Cell>
                            </>
                          )}
                          <DataTable.Cell>
                            {phone || <span style={{ color: 'var(--sk-text-tertiary)' }}>—</span>}
                          </DataTable.Cell>
                          <DataTable.Cell>
                            <span style={{ color: 'var(--sk-text-tertiary)', fontSize: 'var(--sk-text-xs)' }}>
                              {new Date(lead.created_at).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true,
                              })}
                            </span>
                          </DataTable.Cell>
                        </DataTable.Row>
                      );
                    })
                  )}
                </DataTable.Body>
              </DataTable.Table>
            </DataTable.Wrapper>

            {/* Pagination */}
            {totalLeads > 25 && (
              <Card.Footer>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sk-space-3)' }}>
                  <Button variant="ghost" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                    Previous
                  </Button>
                  <span style={{ color: 'var(--sk-text-secondary)', fontSize: 'var(--sk-text-sm)' }}>
                    Page {page} of {Math.ceil(totalLeads / 25)}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={page >= Math.ceil(totalLeads / 25)}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </Card.Footer>
            )}
          </DataTable.Card>
        </PageWrapper>
      </CampaignDetailsProvider>

      {/* Lead Details Drawer - renders inline for proper theme reactivity */}
      <LeadDetailsDrawer open={drawerOpen} onClose={handleDrawerClose} lead={selectedLead} />
    </>
  );
};

export default memo(CampaignDetails);
